import type { Exercise } from "@/types/puzzle";

// The puzzle FEATURE (student player + tutor builder) now runs entirely on real
// GraphQL (see @/lib/puzzles). The only mock content that remains is the public
// marketing homepage demo below, which has no backend.

// The homepage demo is server-rendered, so its scramble must come out the same on
// the server and in the browser: a Math.random order differed 23 loads in 24,
// and React then discarded the server HTML. A scramble seeded by a fixed string
// is still a scramble (the reorder sentence isn't shown solved, the pairs don't
// line up) but every render agrees on it.
export function fixedShuffle<T>(array: T[], seed: string): T[] {
  let state = 0;
  for (const char of seed) state = (Math.imul(state, 31) + char.charCodeAt(0)) | 0;
  // mulberry32: a tiny deterministic PRNG, uniform enough for four quiz options.
  const next = () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const copy = [...array];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(next() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

// ── Homepage demo (landing steps 04/05) ───────────────────────────
// Used by HowItWorks + the puzzle-player components on the public landing.
export const homepageDemoExercises: Exercise[] = [
  {
    id: "demo-mcq",
    type: "multiple-choice",
    question: "How do you say 'Thank you' in Spanish?",
    options: ["Hola", "Gracias", "Adiós", "Por favor"],
    correctIndex: 1,
  },
  {
    id: "demo-fib",
    type: "fill-in-the-blank",
    sentence: "Je ___ à la bibliothèque tous les jours.",
    correctAnswer: "vais",
  },
  {
    id: "demo-reorder",
    type: "reorder-words",
    correctSentence: "I would like a cup of coffee",
  },
  {
    id: "demo-match",
    type: "match-pairs",
    pairs: [
      { word: "Bonjour", translation: "Hello" },
      { word: "Merci", translation: "Thank you" },
      { word: "Au revoir", translation: "Goodbye" },
      { word: "S'il vous plaît", translation: "Please" },
    ],
  },
  {
    id: "demo-translate",
    type: "translate-sentence",
    sourceSentence: "I'm going to the library tomorrow.",
    correctTranslation: "Je vais à la bibliothèque demain.",
    sourceLanguage: "English",
    targetLanguage: "French",
  },
  {
    id: "demo-stroke",
    type: "stroke-order",
    character: "火",
    pinyin: "huǒ",
    meaning: "fire",
    strokes: [
      "M 226 483 Q 254 443 285 393 Q 295 375 312 372 Q 324 369 333 381 Q 342 396 342 430 Q 342 472 235 536 Q 220 543 213 543 Q 206 540 204 526 Q 205 513 226 483 Z",
      "M 690 568 Q 677 547 584 441 Q 571 422 592 425 Q 638 449 738 513 Q 762 532 791 543 Q 816 553 805 575 Q 789 597 759 615 Q 731 633 715 629 Q 700 628 704 612 Q 708 590 690 568 Z",
      "M 511 382 Q 520 439 529 618 Q 530 669 548 728 Q 554 741 543 750 Q 521 769 479 785 Q 454 795 434 789 Q 410 779 430 759 Q 464 725 465 690 Q 469 500 448 374 Q 427 209 284 106 Q 244 81 186 49 Q 170 43 167 39 Q 160 32 179 29 Q 197 28 258 48 Q 301 61 365 101 Q 414 131 442 172 Q 484 232 504 341 L 511 382 Z",
      "M 504 341 Q 597 175 715 40 Q 740 9 767 9 Q 843 13 911 17 Q 939 18 940 25 Q 941 32 910 48 Q 753 117 709 157 Q 592 268 511 382 C 493 406 489 367 504 341 Z",
    ],
    medians: [
      [
        [216, 534],
        [299, 440],
        [316, 391],
      ],
      [
        [714, 618],
        [736, 593],
        [744, 571],
        [696, 522],
        [587, 433],
      ],
      [
        [436, 773],
        [475, 753],
        [497, 733],
        [503, 713],
        [488, 443],
        [470, 321],
        [456, 275],
        [418, 195],
        [368, 138],
        [279, 78],
        [174, 36],
      ],
      [
        [513, 375],
        [521, 341],
        [606, 223],
        [689, 127],
        [745, 76],
        [768, 60],
        [934, 25],
      ],
    ],
  },
];
